import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { SectionId } from "./config";

// How long (ms) to trust a click-selected section over the IntersectionObserver.
// A smooth scrollIntoView animates through every section between the current and
// target one; without this the observer would flicker the active item through
// those in-between sections and often settle on a neighbor instead of the clicked
// target. Covers a typical smooth-scroll animation.
const CLICK_SCROLL_SETTLE_MS = 700;

export const useScrollSpy = (
	sectionIds: ReadonlyArray<SectionId>,
	sectionRefs: RefObject<Record<string, HTMLElement | null>>,
	// The section elements only exist once the summary has loaded and rendered.
	// `sectionRefs` is a mutable ref, so populating it never re-runs the effect;
	// without this flip the observer is set up during the loading state (before any
	// section is in the DOM), finds nothing, and never re-attaches — so the nav only
	// ever updates on click. Pass true once the sections are rendered so the effect
	// re-runs and attaches the observer to them.
	enabled: boolean
): { activeId: SectionId | null; setActive: (id: SectionId) => void } => {
	const [activeId, setActiveId] = useState<SectionId | null>(sectionIds[0] ?? null);

	const idsKey = sectionIds.join(",");

	// Timestamp until which observer-driven active updates are ignored (set on click).
	const suppressUntilRef = useRef(0);

	const setActive = useCallback((id: SectionId) => {
		suppressUntilRef.current = Date.now() + CLICK_SCROLL_SETTLE_MS;
		setActiveId(id);
	}, []);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		const ids = idsKey ? (idsKey.split(",") as SectionId[]) : [];

		setActiveId(ids[0] ?? null);

		const elements = ids.map((id) => sectionRefs.current[id]).filter((el): el is HTMLElement => el != null);

		if (elements.length === 0) {
			return;
		}

		const visible = new Set<string>();

		const observer = new IntersectionObserver(
			(entries) => {
				// Always keep the visible set current so it's accurate once suppression ends.
				for (const entry of entries) {
					const id = entry.target.id;
					if (entry.isIntersecting) {
						visible.add(id);
					} else {
						visible.delete(id);
					}
				}

				// During a click-driven scroll, hold the clicked section active.
				if (Date.now() < suppressUntilRef.current) {
					return;
				}

				const topmost = ids.find((id) => visible.has(id));
				if (topmost) {
					setActiveId(topmost);
				}
			},
			{ rootMargin: "0px 0px -60% 0px", threshold: 0 }
		);

		elements.forEach((el) => observer.observe(el));

		return () => observer.disconnect();
	}, [idsKey, sectionRefs, enabled]);

	return { activeId, setActive };
};
